import React, {useState} from 'react';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import {
    Alert,
    Button,
    Center,
    Container,
    Divider,
    Image,
    Paper,
    Stack,
    Text,
    TextInput,
    Title,
} from "@mantine/core";
import {useForm} from "@mantine/form";
import {startAuthentication, type PublicKeyCredentialRequestOptionsJSON} from "@simplewebauthn/browser";
import {notifications} from "@mantine/notifications";
import {PasskeyService, UserService} from "../../client";
import {formatRequestError} from "../../utils/formatRequestError";
import {useAuthStore} from "../../store/authStore";

function Login() {
    const [passkeyLoading, setPasskeyLoading] = useState(false);

    const form = useForm({
        mode: 'uncontrolled',
        initialValues: {
            username: '',
            password: '',
        },
    });

    const tokenStore = useAuthStore(state => state.setToken);
    const passkeySupported = typeof window !== 'undefined' && !!window.PublicKeyCredential;

    const loginWithPassword = (username: string, password: string) => {
        UserService.loginForAccessTokenApiV1UserLoginPost({
            formData: {username, password}
        }).then((token) => {
            tokenStore(token.access_token);
        }).catch((err) => {
            const detail = err?.body?.detail;
            notifications.show({
                color: 'red',
                title: 'Login failed',
                message: typeof detail === 'string' ? detail : 'Invalid username or password',
            });
        });
    };

    const loginWithPasskey = async (username: string) => {
        if (!passkeySupported) {
            notifications.show({
                color: 'yellow',
                title: 'Passkey unavailable',
                message: 'Passkeys require HTTPS (e.g. mkcert for local development).',
            });
            return;
        }
        const trimmedUsername = username.trim();
        setPasskeyLoading(true);
        try {
            const optionsResponse = await PasskeyService.passkeyLoginOptionsApiV1PasskeyLoginOptionsPost(
                trimmedUsername ? {username: trimmedUsername} : {},
            );
            if (!optionsResponse.has_passkey || !optionsResponse.options || !optionsResponse.challenge_token) {
                notifications.show({
                    color: 'red',
                    title: 'Login failed',
                    message: 'No passkey registered for this user.',
                });
                return;
            }
            const token = optionsResponse.challenge_token;
            const credential = await startAuthentication({
                optionsJSON: optionsResponse.options as unknown as PublicKeyCredentialRequestOptionsJSON,
            });
            const access = await PasskeyService.passkeyLoginVerifyApiV1PasskeyLoginVerifyPost({
                credential: credential as unknown as Record<string, unknown>,
                challenge_token: token,
                ...(trimmedUsername ? {username: trimmedUsername} : {}),
            });
            tokenStore(access.access_token);
        } catch (err: unknown) {
            notifications.show({
                color: 'red',
                title: 'Passkey login failed',
                message: formatRequestError(
                    err,
                    'No matching passkey found. Try entering your username, or use the same hostname (localhost vs 127.0.0.1) as when you registered.',
                ),
            });
        } finally {
            setPasskeyLoading(false);
        }
    };

    return (
        <Center mih="100vh" px="md">
            <Container size={420} w="100%">
                <Paper p="xl" withBorder shadow="sm" radius="md">
                    <Stack gap="lg">
                        <Stack gap="xs" align="center">
                            <Image w={72} src="/logo.png" alt="Parkour Camp"/>
                            <Title order={2} fw={600}>Admin Login</Title>
                            <Text c="dimmed" size="sm" ta="center">
                                Sign in to manage camp registrations and check-in.
                            </Text>
                        </Stack>
                        {!passkeySupported && (
                            <Alert color="yellow" title="HTTPS required">
                                Passkey sign-in is only available over HTTPS.
                            </Alert>
                        )}
                        <form onSubmit={form.onSubmit((values) => {
                            loginWithPassword(values.username, values.password);
                        })}>
                            <Stack gap="md">
                                <TextInput
                                    label="Username"
                                    placeholder="username"
                                    key={form.key('username')}
                                    {...form.getInputProps('username')}
                                />
                                <TextInput
                                    label="Password"
                                    placeholder="password"
                                    type="password"
                                    key={form.key('password')}
                                    {...form.getInputProps('password')}
                                />
                                <Button type="submit" fullWidth>
                                    Login
                                </Button>
                                <Divider label="or" labelPosition="center"/>
                                <Button
                                    type="button"
                                    variant="light"
                                    fullWidth
                                    loading={passkeyLoading}
                                    disabled={!passkeySupported}
                                    onClick={() => loginWithPasskey(form.getValues().username)}
                                >
                                    Sign in with passkey
                                </Button>
                            </Stack>
                        </form>
                    </Stack>
                </Paper>
            </Container>
        </Center>
    );
}

export default Login;
